import * as React from "react"
import { graphql, useStaticQuery } from "gatsby"

import { ensureBackgroundAudioElement } from "../lib/background-audio-singleton.js"
import { getLinkHref } from "../lib/prismic-link.js"

/**
 * Controls a single shared `<audio>` on `document.body` (see singleton).
 * Source: `general_website_data.background_music` (media link), else `/audio/background.mp3`.
 * UI re-mounts are safe; the element and currentTime are preserved.
 */
const BackgroundAudio = () => {
  const data = useStaticQuery(graphql`
    query BackgroundAudioPrismicQuery {
      allPrismicGeneralWebsiteData(limit: 1) {
        nodes {
          data {
            background_music {
              url
              raw
            }
          }
        }
      }
    }
  `)

  const musicHref = getLinkHref(
    data?.allPrismicGeneralWebsiteData?.nodes?.[0]?.data?.background_music
  )

  const [mounted, setMounted] = React.useState(false)
  const [playing, setPlaying] = React.useState(false)

  React.useEffect(() => {
    setMounted(true)
  }, [])

  React.useEffect(() => {
    if (!mounted) {
      return
    }
    const audio = ensureBackgroundAudioElement(musicHref)
    if (!audio) {
      return
    }

    setPlaying(!audio.paused)

    const onPlay = () => setPlaying(true)
    const onPause = () => setPlaying(false)
    audio.addEventListener("play", onPlay)
    audio.addEventListener("pause", onPause)

    const tryAutoplay = () => {
      void audio.play().catch(() => {
        /* Browsers often block audible autoplay until a user gesture. */
      })
    }

    tryAutoplay()
    audio.addEventListener("canplay", tryAutoplay, { once: true })

    return () => {
      audio.removeEventListener("play", onPlay)
      audio.removeEventListener("pause", onPause)
      audio.removeEventListener("canplay", tryAutoplay)
    }
  }, [mounted, musicHref])

  const toggle = React.useCallback(() => {
    const audio = ensureBackgroundAudioElement(musicHref)
    if (!audio) {
      return
    }
    if (audio.paused) {
      void audio.play().catch(() => {})
    } else {
      audio.pause()
    }
  }, [musicHref])

  if (!mounted) {
    return null
  }

  return (
    <div className="fixed bottom-4 right-4 z-[60] max-[50rem]:bottom-3 max-[50rem]:right-3">
      <button
        type="button"
        onClick={toggle}
        className="inline-flex h-11 w-11 items-center justify-center rounded-full border border-neutral-300 bg-white/95 text-neutral-900 shadow-md backdrop-blur-sm transition hover:bg-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-neutral-900"
        aria-pressed={playing}
        aria-label={playing ? "Pause background music" : "Play background music"}
      >
        {playing ? (
          <svg
            viewBox="0 0 24 24"
            className="h-5 w-5"
            aria-hidden="true"
            focusable="false"
          >
            <rect x="6.5" y="5.5" width="4" height="13" rx="1" />
            <rect x="13.5" y="5.5" width="4" height="13" rx="1" />
          </svg>
        ) : (
          <svg
            viewBox="0 0 24 24"
            className="h-5 w-5"
            aria-hidden="true"
            focusable="false"
          >
            <path d="M9 7.5v9l8-4.5-8-4.5Z" />
          </svg>
        )}
      </button>
    </div>
  )
}

export default BackgroundAudio
